import { DateField } from "../components/DateField";
import { useEffect, useState } from "react";
import { Banner, Button, Dialog, Field, Input, PageHead, Panel, SelectX } from "@guestpilot/ui-kit";
import { ArrowDownload16Regular } from "@fluentui/react-icons";
import { useGet, useGetPages, useWrite } from "../api/hooks";
import type { ExportJob, PersonRef, Schema, Team } from "../api/types";
import { PersonPicker } from "../components/PersonPicker";
import {
  ParticipantDrawer,
  ParticipantsTable,
  RemovalDialog,
  type Participant,
  type Reason,
} from "../components/Participants";
import { RISK_ORDER } from "../components/TeamsTable";
import { fieldErrorsOf, useHandleApiError } from "../errors";
import { useI18n } from "../i18n";
import { useSession } from "../session";
import { useToast } from "@guestpilot/ui-kit";

type Bulk = Schema<"BulkResult">;

/** The global inventory: every external user across the Teams the person may see, searchable and filterable (BR-007, BR-030, BR-199). */
export function ExternalUsers() {
  const { t } = useI18n();
  const s = useSession();
  const toast = useToast();
  const fail = useHandleApiError();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [team, setTeam] = useState("");
  const [risk, setRisk] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(h);
  }, [q]);
  const teams = useGet<Team[]>("/api/v1/teams", { filter: "all" });
  const list = useGetPages<Participant>("/api/v1/participants", {
    q: debounced || undefined,
    teamId: team || undefined,
    risk: risk || undefined,
    limit: 50,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [edit, setEdit] = useState<Participant | null>(null);
  const [remove, setRemove] = useState<Participant | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const exportAll = useWrite<ExportJob, Schema<"ExportCreate">>("POST", "/api/v1/exports", [
    "/api/v1/exports",
  ]);

  useEffect(() => setSelected(new Set()), [debounced, team, risk]);

  return (
    <div className="gp-page">
      <PageHead
        title={t("ui:users.title")}
        subtitle={t("ui:users.total", { n: list.total ?? list.items?.length ?? 0 })}
      />
      <div className="gp-row" role="search" aria-label={t("ui:users.filters")}>
        <div style={{ minWidth: "calc(var(--space-64) * 4)" }}>
          <Input
            type="search"
            aria-label={t("ui:users.search")}
            placeholder={t("ui:users.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <SelectX
          aria-label={t("ui:users.col.team")}
          value={team}
          onChange={(e) => setTeam(e.target.value)}
          options={[
            { value: "", label: t("ui:users.allTeams") },
            ...(teams.data ?? []).map((x) => ({ value: x.id, label: x.displayName })),
          ]}
        />
        <SelectX
          aria-label={t("ui:users.col.risk")}
          value={risk}
          onChange={(e) => setRisk(e.target.value)}
          options={[
            { value: "", label: t("ui:users.anyRisk") },
            ...RISK_ORDER.map((r) => ({ value: r, label: t(`ui:risk.${r}`) })),
          ]}
        />
        <span className="gp-spacer" />
        <Button
          disabledReason={s.can("EXPORT_CREATE") ? undefined : t("ui:gate.noPermission")}
          onClick={() =>
            exportAll.mutate(
              { body: { scope: "all", format: "csv" } },
              { onSuccess: () => toast.success(t("ui:exports.queued")), onError: fail },
            )
          }
        >
          <ArrowDownload16Regular aria-hidden /> {t("ui:users.exportCsv")}
        </Button>
      </div>
      <Panel label={t("ui:users.title")}>
        <ParticipantsTable
          rows={list.items}
          loading={list.isLoading}
          showTeam
          sticky
          selectable={s.can("PARTICIPANT_BULK_EDIT")}
          selected={selected}
          onSelect={setSelected}
          onEdit={setEdit}
          onRemove={setRemove}
          bulkBar={
            <>
              <span>{t("ui:users.selected", { n: selected.size })}</span>
              <Button
                variant="ghost"
                disabledReason={s.gate("PARTICIPANT_BULK_EDIT")}
                onClick={() => setBulkOpen(true)}
              >
                {t("ui:users.bulk")}
              </Button>
            </>
          }
        />
        {list.hasNextPage ? (
          <div className="gp-actions" style={{ padding: "var(--space-12)", justifyContent: "center" }}>
            <Button onClick={() => void list.fetchNextPage()}>{t("ui:users.loadMore")}</Button>
          </div>
        ) : null}
      </Panel>
      <ParticipantDrawer participant={edit} onClose={() => setEdit(null)} />
      <RemovalDialog participant={remove} onClose={() => setRemove(null)} />
      <BulkDialog
        open={bulkOpen}
        ids={[...selected]}
        onClose={() => setBulkOpen(false)}
        onDone={() => setSelected(new Set())}
      />
    </div>
  );
}

/** Bulk assign sponsor, set expiry, set reason: each participant is audited on its own; failures are listed (BR-205). */
function BulkDialog({
  open,
  ids,
  onClose,
  onDone,
}: {
  open: boolean;
  ids: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const fail = useHandleApiError();
  const reasons = useGet<Reason[]>("/api/v1/access-reasons", undefined, { enabled: open });
  const bulk = useWrite<Bulk, Schema<"BulkParticipantUpdate">>("POST", "/api/v1/participants/bulk", [
    "/api/v1/participants",
    "/api/v1/teams",
    "/api/v1/dashboard",
  ]);
  const [sponsor, setSponsor] = useState<PersonRef | null>(null);
  const [expiry, setExpiry] = useState("");
  const [reason, setReason] = useState("");
  const [result, setResult] = useState<Bulk | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const patch = {
    ...(sponsor ? { sponsorPersonId: sponsor.id } : {}),
    ...(expiry ? { expiryDate: expiry } : {}),
    ...(reason ? { accessReasonId: reason } : {}),
  };
  const empty = Object.keys(patch).length === 0;
  return (
    <Dialog
      open={open}
      onClose={() => {
        setResult(null);
        onClose();
      }}
      title={t("ui:bulk.title", { n: ids.length })}
      description={t("ui:bulk.lead")}
      footer={
        result ? (
          <Button
            variant="primary"
            onClick={() => {
              setResult(null);
              onClose();
            }}
          >
            {t("common:close")}
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>{t("common:cancel")}</Button>
            <Button
              variant="primary"
              disabledReason={empty ? t("ui:bulk.nothing") : undefined}
              onClick={() =>
                bulk.mutate(
                  { body: { participantIds: ids, patch } },
                  {
                    onSuccess: (r) => {
                      setResult(r);
                      onDone();
                    },
                    onError: (e) => {
                      setErrors(fieldErrorsOf(e));
                      fail(e);
                    },
                  },
                )
              }
            >
              {t("ui:bulk.apply")}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <>
          <Banner tone={result.failed?.length ? "warn" : "success"}>
            {t("ui:bulk.result", { n: result.updated ?? 0, failed: result.failed?.length ?? 0 })}
          </Banner>
          {(result.failed ?? []).length > 0 ? (
            <ul className="t-body-sm" style={{ margin: 0 }}>
              {(result.failed ?? []).map((f) => (
                <li key={f.participantId}>{f.code ? t(`errors:${f.code}`) : f.participantId}</li>
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        <>
          <Field label={t("ui:users.col.sponsor")} error={errors["sponsorPersonId"]}>
            <PersonPicker label={t("ui:users.col.sponsor")} value={sponsor} onChange={setSponsor} />
          </Field>
          <Field label={t("ui:users.col.expiry")} error={errors["expiryDate"]}>
            <DateField value={expiry} onChange={(e) => setExpiry(e.target.value)} />
          </Field>
          <Field label={t("ui:users.col.reason")}>
            <SelectX
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              options={[
                { value: "", label: t("ui:bulk.keepReason") },
                ...(reasons.data ?? [])
                  .filter((r) => !r.retired)
                  .map((r) => ({ value: r.id ?? "", label: r.label ?? "" })),
              ]}
            />
          </Field>
        </>
      )}
    </Dialog>
  );
}
